import React, { useState } from "react";
import { Card, ListGroup, Button } from "react-bootstrap";
import { collection, deleteDoc, doc, getDocs, query, where } from "@firebase/firestore";
import { db, functions } from "../../../firebase/FirebaseProvider";
import { httpsCallable } from "@firebase/functions";
import { BadgeStatus, BadgeType, User, UserBadge } from "../../types/Types";
import { Link } from "react-router-dom";
import PageTracking from "../../auth/PageTracking";
import SuperAdminNav from "./SuperAdminNav";
import SuperAdminUserAnalysis from "./SuperAdminUserAnalysis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faBeerMugEmpty, faScrewdriverWrench, faTrophy, faPassport, faKey, faCheckCircle, faLocationDot } from "@fortawesome/free-solid-svg-icons";

interface DuplicateUserEmail {
    email: string;
    userCount: number;
    authCount: number;
    users: Array<User>;
    auths?: Array<DuplicateAuth>;
}

interface DuplicateAuth {
    id: string;
    userId?: string;
}

interface AuthRecord {
    id: string;
    email: string;
    userId?: string;
}

function SuperAdminUsers() {
    const [inactiveUsers, setInactiveUsers] = useState<User[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [duplicateUsers, setDuplicateUsers] = useState<DuplicateUserEmail[]>([]);
    const [usersNotAuthed, setUsersNotAuthed] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [duplicateLoading, setDuplicateLoading] = useState<boolean>(false);
    const [duplicateLoaded, setDuplicateLoaded] = useState<boolean>(false);
    const [usersNotAuthedLoading, setUsersNotAuthedLoading] = useState<boolean>(false);
    const [usersNotAuthedLoaded, setUsersNotAuthedLoaded] = useState<boolean>(false);
    const [deletingDuplicateUserIds, setDeletingDuplicateUserIds] = useState<Record<string, boolean>>({});
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<string | null>('ACTIVE');
    const [fastMode, setFastMode] = useState<boolean>(false);
    const [showBadges, setShowBadges] = useState<boolean>(false);
    const [buttonStates, setButtonStates] = useState<Record<string, { activating?: boolean; deactivating?: boolean; activated?: boolean; deactivated?: boolean; addingAdminBadge?: boolean; adminBadgeAdded?: boolean; addingChampionBadge?: boolean; championBadgeAdded?: boolean; addingSupporterBadge?: boolean; supporterBadgeAdded?: boolean; addingPassportBadge?: boolean; passportBadgeAdded?: boolean }>>({});

    const handleLoad = () => {
        setIsExpanded(true);
        loadUsers();
    };

    const normalizeEmail = (email: any): string => {
        return typeof email === 'string' ? email.trim().toLowerCase() : '';
    };

    const getStringValue = (value: any): string | undefined => {
        if (typeof value === 'string') {
            return value;
        }
        if (value && typeof value.id === 'string') {
            return value.id;
        }
        return undefined;
    };

    const loadDuplicateUsers = async () => {
        try {
            setDuplicateLoading(true);
            setDuplicateLoaded(true);

            const allUsersSnapshot = await getDocs(collection(db, 'users'));
            const authsSnapshot = await getDocs(collection(db, 'auths'));
            const usersByEmail = new Map<string, Array<User>>();
            const authsByEmail = new Map<string, Array<DuplicateAuth>>();

            allUsersSnapshot.forEach((userDoc) => {
                const user = { ...(userDoc.data() as User), id: userDoc.id };
                const normalizedEmail = normalizeEmail(user.email);
                if (!normalizedEmail) return;

                if (!usersByEmail.has(normalizedEmail)) {
                    usersByEmail.set(normalizedEmail, []);
                }
                usersByEmail.get(normalizedEmail)?.push(user);
            });

            authsSnapshot.forEach((authDoc) => {
                const authData = authDoc.data();
                const normalizedEmail = normalizeEmail(authData.email);
                if (!normalizedEmail) return;

                if (!authsByEmail.has(normalizedEmail)) {
                    authsByEmail.set(normalizedEmail, []);
                }
                authsByEmail.get(normalizedEmail)?.push({
                    id: authDoc.id,
                    userId: getStringValue(authData.userId)
                });
            });

            const emails = new Set<string>([
                ...Array.from(usersByEmail.keys()),
                ...Array.from(authsByEmail.keys())
            ]);
            const duplicateEmails = Array.from(emails)
                .map((email) => {
                    const users = usersByEmail.get(email) || [];
                    const auths = authsByEmail.get(email) || [];
                    return {
                        email,
                        userCount: users.length,
                        authCount: auths.length,
                        users: users.sort((a, b) => getStringValue(a.name)?.localeCompare(getStringValue(b.name) || '') || 0),
                        auths: auths.sort((a, b) => a.id.localeCompare(b.id))
                    };
                })
                .filter((duplicateEmail) => duplicateEmail.userCount > 1 || duplicateEmail.authCount > 1)
                .sort((a, b) => a.email.localeCompare(b.email));

            setDuplicateUsers(duplicateEmails);
        } catch (error) {
            console.error("Error loading duplicate users:", error);
        } finally {
            setDuplicateLoading(false);
        }
    };

    const handleDeleteDuplicateUser = async (user: User) => {
        if ((user.visitCount || 0) > 0) {
            return;
        }

        try {
            setDeletingDuplicateUserIds(prev => ({ ...prev, [user.id]: true }));
            await deleteDoc(doc(db, 'users', user.id));

            setDuplicateUsers(prev => prev
                .map((duplicateUser) => {
                    const users = duplicateUser.users.filter((duplicateUserRecord) => duplicateUserRecord.id !== user.id);
                    return {
                        ...duplicateUser,
                        users,
                        userCount: users.length
                    };
                })
                .filter((duplicateUser) => duplicateUser.userCount > 1 || duplicateUser.authCount > 1)
            );

            setAllUsers(prev => prev.filter((existingUser) => existingUser.id !== user.id));
            setInactiveUsers(prev => prev.filter((existingUser) => existingUser.id !== user.id));
        } catch (error: any) {
            console.error("Error deleting duplicate user:", error);
            alert(`Failed to delete user: ${error.message}`);
        } finally {
            setDeletingDuplicateUserIds(prev => ({ ...prev, [user.id]: false }));
        }
    };

    const loadUsersNotAuthed = async () => {
        try {
            setUsersNotAuthedLoading(true);
            setUsersNotAuthedLoaded(true);

            const allUsersSnapshot = await getDocs(collection(db, 'users'));
            const authsSnapshot = await getDocs(collection(db, 'auths'));
            const users: Array<User> = [];
            const authsByEmail = new Map<string, Array<AuthRecord>>();

            allUsersSnapshot.forEach((userDoc) => {
                users.push({ ...(userDoc.data() as User), id: userDoc.id });
            });

            authsSnapshot.forEach((authDoc) => {
                const authData = authDoc.data();
                const normalizedEmail = normalizeEmail(authData.email);
                if (!normalizedEmail) return;

                if (!authsByEmail.has(normalizedEmail)) {
                    authsByEmail.set(normalizedEmail, []);
                }
                authsByEmail.get(normalizedEmail)?.push({
                    id: authDoc.id,
                    email: normalizedEmail,
                    userId: getStringValue(authData.userId)
                });
            });

            const usersWithoutSameEmailAuth = users
                .filter((user) => {
                    const normalizedEmail = normalizeEmail(user.email);
                    if (!normalizedEmail) return true;

                    const matchingEmailAuths = authsByEmail.get(normalizedEmail) || [];
                    return !matchingEmailAuths.some((authItem) => authItem.userId === user.id);
                })
                .sort((a, b) => normalizeEmail(a.email).localeCompare(normalizeEmail(b.email)));

            setUsersNotAuthed(usersWithoutSameEmailAuth);
        } catch (error) {
            console.error("Error loading users not authed:", error);
        } finally {
            setUsersNotAuthedLoading(false);
        }
    };

    const handleDeactivate = async (userId: string, userName: string, userIndex?: number) => {
        const usersToProcess: User[] = [inactiveUsers.find(u => u.id === userId)!];
        
        // If fast mode is enabled and on NOT_SEEN tab, get next 10 users with ACTIVE status
        if (fastMode && statusFilter === 'NOT_SEEN' && userIndex !== undefined) {
            for (let i = userIndex + 1; i < Math.min(userIndex + 11, inactiveUsers.length); i++) {
                const nextUser = inactiveUsers[i];
                if (nextUser.status === 'ACTIVE') {
                    usersToProcess.push(nextUser);
                }
            }
        }
        
        try {
            // Set all as deactivating
            const updatedStates: Record<string, any> = {};
            usersToProcess.forEach(user => {
                updatedStates[user.id] = { deactivating: true };
            });
            setButtonStates(prev => ({ ...prev, ...updatedStates }));
            
            const setUserInactive = httpsCallable(functions, 'setUserInactive');
            
            // Process all users
            for (const user of usersToProcess) {
                await setUserInactive({ userId: user.id });
                updatedStates[user.id] = { deactivated: true };
                setButtonStates(prev => ({ ...prev, [user.id]: { deactivated: true } }));
            }
            
            // Update the local user state - hide from list if on ACTIVE tab
            if (statusFilter === 'ACTIVE') {
                setInactiveUsers(prev => prev.filter(user =>
                    !usersToProcess.find(u => u.id === user.id)
                ));
            } else {
                setInactiveUsers(prev => prev.map(user =>
                    usersToProcess.find(u => u.id === user.id) ? { ...user, status: 'INACTIVE' } : user
                ));
            }
        } catch (error: any) {
            console.error('Error deactivating user:', error);
            alert(`Failed to deactivate user: ${error.message}`);
            usersToProcess.forEach(user => {
                setButtonStates(prev => ({ ...prev, [user.id]: {} }));
            });
        }
    };

    const handleActivate = async (userId: string, userName: string, userIndex?: number) => {
        const usersToProcess: User[] = [inactiveUsers.find(u => u.id === userId)!];
        
        // If fast mode is enabled and on NOT_SEEN tab, get next 10 users with ACTIVE status
        if (fastMode && statusFilter === 'NOT_SEEN' && userIndex !== undefined) {
            for (let i = userIndex + 1; i < Math.min(userIndex + 11, inactiveUsers.length); i++) {
                const nextUser = inactiveUsers[i];
                if (nextUser.status === 'ACTIVE') {
                    usersToProcess.push(nextUser);
                }
            }
        }
        
        try {
            // Set all as activating
            const updatedStates: Record<string, any> = {};
            usersToProcess.forEach(user => {
                updatedStates[user.id] = { activating: true };
            });
            setButtonStates(prev => ({ ...prev, ...updatedStates }));
            
            const setUserActive = httpsCallable(functions, 'setUserActive');
            
            // Process all users
            for (const user of usersToProcess) {
                await setUserActive({ userId: user.id });
                updatedStates[user.id] = { activated: true };
                setButtonStates(prev => ({ ...prev, [user.id]: { activated: true } }));
            }
            
            // Update the local user state
            setInactiveUsers(prev => prev.map(user => 
                usersToProcess.find(u => u.id === user.id) ? { ...user, status: 'ACTIVE' } : user
            ));
        } catch (error: any) {
            console.error('Error activating user:', error);
            alert(`Failed to activate user: ${error.message}`);
            usersToProcess.forEach(user => {
                setButtonStates(prev => ({ ...prev, [user.id]: {} }));
            });
        }
    };

    const handleSetPassword = async (user: User) => {
        if (!window.confirm(`Are you sure you want to reset the password for ${user.name}?`)) return;
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let newPassword = '';
        for (let i = 0; i < 6; i++) {
            newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        try {
            const setUserPassword = httpsCallable(functions, 'setUserPassword');
            await setUserPassword({ userId: user.id, newPassword });
            alert(`Password for ${user.name} set to: ${newPassword}`);
        } catch (error: any) {
            console.error('Error setting password:', error);
            alert(`Failed to set password: ${error.message}`);
        }
    };

    const handleAddAdminBadge = async (user: User) => {
        try {
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingAdminBadge: true }
            }));

            const addUserBadge = httpsCallable(functions, 'addUserBadge');
            const response = await addUserBadge({
                targetUserId: user.id,
                badgeType: BadgeType.ADMIN,
                description: "'Spoons Tracker Admin",
                order: 1,
                status: BadgeStatus.ACTIVE
            });

            const badgeId = (response.data as any)?.badgeId as string | undefined;
            const newBadge: UserBadge = {
                id: badgeId,
                userId: user.id,
                badgeType: BadgeType.ADMIN,
                description: "'Spoons Tracker Admin",
                createdAt: new Date(),
                status: BadgeStatus.ACTIVE,
                order: 1
            };

            setAllUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));
            setInactiveUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));

            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingAdminBadge: false, adminBadgeAdded: true }
            }));
        } catch (error: any) {
            console.error('Error adding admin badge:', error);
            alert(`Failed to add admin badge: ${error.message}`);
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingAdminBadge: false }
            }));
        }
    };

    const handleAddChampionBadge = async (user: User) => {
        try {
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingChampionBadge: true }
            }));

            const addUserBadge = httpsCallable(functions, 'addUserBadge');
            const currentYear = new Date().getFullYear();
            const response = await addUserBadge({
                targetUserId: user.id,
                badgeType: BadgeType.CHAMPION,
                description: `'Spoons Tracker Champion ${currentYear}`,
                order: 1,
                status: BadgeStatus.ACTIVE
            });

            const badgeId = (response.data as any)?.badgeId as string | undefined;
            const newBadge: UserBadge = {
                id: badgeId,
                userId: user.id,
                badgeType: BadgeType.CHAMPION,
                description: `'Spoons Tracker Champion ${currentYear}`,
                createdAt: new Date(),
                status: BadgeStatus.ACTIVE,
                order: 1
            };

            setAllUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));
            setInactiveUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));

            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingChampionBadge: false, championBadgeAdded: true }
            }));
        } catch (error: any) {
            console.error('Error adding champion badge:', error);
            alert(`Failed to add champion badge: ${error.message}`);
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingChampionBadge: false }
            }));
        }
    };

    const handleAddSupporterBadge = async (user: User) => {
        try {
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingSupporterBadge: true }
            }));

            const addUserBadge = httpsCallable(functions, 'addUserBadge');
            const response = await addUserBadge({
                targetUserId: user.id,
                badgeType: BadgeType.SUPPORTER,
                description: "'Spoons Tracker Supporter",
                order: 1,
                status: BadgeStatus.ACTIVE
            });

            const badgeId = (response.data as any)?.badgeId as string | undefined;
            const newBadge: UserBadge = {
                id: badgeId,
                userId: user.id,
                badgeType: BadgeType.SUPPORTER,
                description: "'Spoons Tracker Supporter",
                createdAt: new Date(),
                status: BadgeStatus.ACTIVE,
                order: 1
            };

            setAllUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));
            setInactiveUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));

            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingSupporterBadge: false, supporterBadgeAdded: true }
            }));
        } catch (error: any) {
            console.error('Error adding supporter badge:', error);
            alert(`Failed to add supporter badge: ${error.message}`);
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingSupporterBadge: false }
            }));
        }
    };

    const handleAddPassportBadge = async (user: User) => {
        try {
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingPassportBadge: true }
            }));

            const addUserBadge = httpsCallable(functions, 'addUserBadge');
            const response = await addUserBadge({
                targetUserId: user.id,
                badgeType: BadgeType.PASSPORT,
                description: "All countries visited",
                order: 1,
                status: BadgeStatus.ACTIVE
            });

            const badgeId = (response.data as any)?.badgeId as string | undefined;
            const newBadge: UserBadge = {
                id: badgeId,
                userId: user.id,
                badgeType: BadgeType.PASSPORT,
                description: "All countries visited",
                createdAt: new Date(),
                status: BadgeStatus.ACTIVE,
                order: 1
            };

            setAllUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));
            setInactiveUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, badges: [...(u.badges || []), newBadge] } : u
            ));

            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingPassportBadge: false, passportBadgeAdded: true }
            }));
        } catch (error: any) {
            console.error('Error adding passport badge:', error);
            alert(`Failed to add passport badge: ${error.message}`);
            setButtonStates(prev => ({
                ...prev,
                [user.id]: { ...prev[user.id], addingPassportBadge: false }
            }));
        }
    };

    const loadUsers = async () => {
        try {
            setLoading(true);

            const allUsersQuery = query(collection(db, 'users'));
            const allUsersSnapshot = await getDocs(allUsersQuery);
            const allUsers: User[] = [];
            const seenIds = new Set<string>();
            
            allUsersSnapshot.forEach((doc) => {
                if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    const userData = doc.data() as User;
                    allUsers.push({...userData, id: doc.id});
                } else {
                    console.warn(`Duplicate user found: ${doc.id}`);
                }
            });

            // Sort by visit count descending (highest first)
            allUsers.sort((a, b) => {
                const aVisits = a.visitCount || 0;
                const bVisits = b.visitCount || 0;
                return bVisits - aVisits;
            });

            const adminBadgesQuery = query(
                collection(db, 'user-badges'),
                where('badgeType', '==', BadgeType.ADMIN)
            );
            const adminBadgesSnapshot = await getDocs(adminBadgesQuery);
            const adminBadgesByUserId = new Map<string, Array<UserBadge>>();

            adminBadgesSnapshot.forEach((doc) => {
                const badge = { ...doc.data(), id: doc.id } as UserBadge;
                const userId = badge.userId;
                if (!adminBadgesByUserId.has(userId)) {
                    adminBadgesByUserId.set(userId, []);
                }
                adminBadgesByUserId.get(userId)?.push(badge);
            });

            const championBadgesQuery = query(
                collection(db, 'user-badges'),
                where('badgeType', '==', BadgeType.CHAMPION)
            );
            const championBadgesSnapshot = await getDocs(championBadgesQuery);
            const championBadgesByUserId = new Map<string, Array<UserBadge>>();

            championBadgesSnapshot.forEach((doc) => {
                const badge = { ...doc.data(), id: doc.id } as UserBadge;
                const userId = badge.userId;
                if (!championBadgesByUserId.has(userId)) {
                    championBadgesByUserId.set(userId, []);
                }
                championBadgesByUserId.get(userId)?.push(badge);
            });

            const supporterBadgesQuery = query(
                collection(db, 'user-badges'),
                where('badgeType', '==', BadgeType.SUPPORTER)
            );
            const supporterBadgesSnapshot = await getDocs(supporterBadgesQuery);
            const supporterBadgesByUserId = new Map<string, Array<UserBadge>>();

            supporterBadgesSnapshot.forEach((doc) => {
                const badge = { ...doc.data(), id: doc.id } as UserBadge;
                const userId = badge.userId;
                if (!supporterBadgesByUserId.has(userId)) {
                    supporterBadgesByUserId.set(userId, []);
                }
                supporterBadgesByUserId.get(userId)?.push(badge);
            });

            const passportBadgesQuery = query(
                collection(db, 'user-badges'),
                where('badgeType', '==', BadgeType.PASSPORT)
            );
            const passportBadgesSnapshot = await getDocs(passportBadgesQuery);
            const passportBadgesByUserId = new Map<string, Array<UserBadge>>();

            passportBadgesSnapshot.forEach((doc) => {
                const badge = { ...doc.data(), id: doc.id } as UserBadge;
                const userId = badge.userId;
                if (!passportBadgesByUserId.has(userId)) {
                    passportBadgesByUserId.set(userId, []);
                }
                passportBadgesByUserId.get(userId)?.push(badge);
            });

            allUsers.forEach(user => {
                const adminBadges = adminBadgesByUserId.get(user.id) || [];
                const championBadges = championBadgesByUserId.get(user.id) || [];
                const supporterBadges = supporterBadgesByUserId.get(user.id) || [];
                const passportBadges = passportBadgesByUserId.get(user.id) || [];
                user.badges = [...(user.badges || []), ...adminBadges, ...championBadges, ...supporterBadges, ...passportBadges];
            });

            console.log(`Loaded users: ${allUsers.length} documents (snapshot size: ${allUsersSnapshot.size})`);
            setAllUsers(allUsers);
            // Filter by ACTIVE by default
            setInactiveUsers(allUsers.filter(user => user.status === 'ACTIVE'));
        } catch (error) {
            console.error("Error loading users:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filter: string | null) => {
        setStatusFilter(filter);
        if (filter === null) {
            setInactiveUsers(allUsers);
        } else if (filter === 'UNKNOWN') {
            setInactiveUsers(allUsers.filter(user => !user.status || (user.status !== 'ACTIVE' && user.status !== 'INACTIVE')));
        } else if (filter === 'NOT_SEEN') {
            const now = new Date();
            const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            const notSeenUsers = allUsers.filter(user => {
                if (user.status !== 'ACTIVE') return false;
                
                // If no lastSeenAt, only include if they have no sessions
                if (!user.lastSeenAt) {
                    return (user.sessionCount || 0) === 0;
                }
                
                // If they have lastSeenAt, check if it's older than 1 year
                const lastSeenAny = user.lastSeenAt as any;
                const lastSeen = lastSeenAny?.toDate ? lastSeenAny.toDate() : user.lastSeenAt;
                return lastSeen && lastSeen < oneYearAgo;
            });
            // Sort by visit count ascending (lowest first)
            notSeenUsers.sort((a, b) => {
                const aVisits = a.visitCount || 0;
                const bVisits = b.visitCount || 0;
                return aVisits - bVisits;
            });
            setInactiveUsers(notSeenUsers);
        } else if (filter === 'CHAMPION') {
            setInactiveUsers(allUsers.filter(user => user.champion));
        } else if (filter === 'SUPPORTER') {
            setInactiveUsers(allUsers.filter(user => user.supporter));
        } else {
            setInactiveUsers(allUsers.filter(user => user.status === filter));
        }
    };

    const formatDate = (date: Date | undefined): string => {
        if (!date) return 'Unknown';
        
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return '1 day ago';
        if (diffDays < 7) return `${diffDays} days ago`;
        
        return date.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <PageTracking />
            <SuperAdminNav />

            <SuperAdminUserAnalysis />

            <Card className={"mb-3"}>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div><b>Duplicate Users</b></div>
                            {duplicateLoaded && !duplicateLoading && (
                                <small className="text-muted">Total: {duplicateUsers.length} duplicate email addresses</small>
                            )}
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={loadDuplicateUsers}
                            disabled={duplicateLoading}
                        >
                            {duplicateLoading ? "Loading..." : "Load"}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body style={{ padding: 0 }}>
                    {!duplicateLoaded ? (
                        <div className="text-center p-3">Press Load to check duplicate users</div>
                    ) : duplicateLoading ? (
                        <div className="text-center p-3">Loading...</div>
                    ) : duplicateUsers.length === 0 ? (
                        <div className="text-center p-3">No duplicate users</div>
                    ) : (
                        <ListGroup variant="flush">
                            {duplicateUsers.map((duplicateUser) => (
                                <ListGroup.Item key={duplicateUser.email}>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <b>{duplicateUser.email}</b>
                                            <div>
                                                <small className="text-muted">
                                                    Users: <b>{duplicateUser.userCount}</b> | Auths: <b>{duplicateUser.authCount}</b>
                                                </small>
                                            </div>
                                            {duplicateUser.users.length > 0 && (
                                                <div className="mt-1">
                                                    {duplicateUser.users.map((user) => (
                                                        <div
                                                            key={user.id}
                                                            className={(duplicateUser.auths || []).some((authItem) => authItem.userId === user.id) ? "bg-warning px-1" : undefined}
                                                        >
                                                            <small>
                                                                <Link to={`/profile/${user.id}/visits`} className="text-decoration-none">
                                                                    {user.name || 'Unnamed user'}
                                                                </Link>
                                                                <span className="text-muted"> ({user.id})</span>
                                                                {(duplicateUser.auths || []).some((authItem) => authItem.userId === user.id) && (
                                                                    <span className="ml-2 badge badge-warning">linked auth</span>
                                                                )}
                                                                <span className="text-muted ml-2">
                                                                    <FontAwesomeIcon icon={faCheckCircle} title="Visits" /> <b>{user.visitCount || 0}</b> | <FontAwesomeIcon icon={faLocationDot} title="Check-ins" /> <b>{Math.floor(user.sessionCount || 0)}</b>
                                                                </span>
                                                                {(user.visitCount || 0) === 0 && (
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        className="ml-2 py-0 px-1"
                                                                        onClick={() => handleDeleteDuplicateUser(user)}
                                                                        disabled={deletingDuplicateUserIds[user.id]}
                                                                    >
                                                                        {deletingDuplicateUserIds[user.id] ? "Deleting..." : <FontAwesomeIcon icon={faTrash} />}
                                                                    </Button>
                                                                )}
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            {(duplicateUser.auths || []).map((authItem) => (
                                                <div key={authItem.id}>
                                                    <small className="text-muted">
                                                        {authItem.id}
                                                        {authItem.userId && (
                                                            <span>{" -> "}{authItem.userId}</span>
                                                        )}
                                                    </small>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            <Card className={"mb-3"}>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div><b>Users not authed</b></div>
                            {usersNotAuthedLoaded && !usersNotAuthedLoading && (
                                <small className="text-muted">Total: {usersNotAuthed.length} users</small>
                            )}
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={loadUsersNotAuthed}
                            disabled={usersNotAuthedLoading}
                        >
                            {usersNotAuthedLoading ? "Loading..." : "Load"}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body style={{ padding: 0 }}>
                    {!usersNotAuthedLoaded ? (
                        <div className="text-center p-3">Press Load to check users not linked to auth</div>
                    ) : usersNotAuthedLoading ? (
                        <div className="text-center p-3">Loading...</div>
                    ) : usersNotAuthed.length === 0 ? (
                        <div className="text-center p-3">No users not authed</div>
                    ) : (
                        <ListGroup variant="flush">
                            {usersNotAuthed.map((user) => (
                                <ListGroup.Item key={user.id}>
                                    <div>
                                        <Link to={`/profile/${user.id}/visits`} className="text-decoration-none">
                                            <b>{user.name || 'Unnamed user'}</b>
                                        </Link>
                                        {user.email && <span className="text-muted ml-2">({user.email})</span>}
                                        <span className="text-muted ml-2">({user.id})</span>
                                    </div>
                                    <div>
                                        <small className="text-muted">
                                            <FontAwesomeIcon icon={faCheckCircle} title="Visits" /> <b>{user.visitCount || 0}</b> | <FontAwesomeIcon icon={faLocationDot} title="Check-ins" /> <b>{Math.floor(user.sessionCount || 0)}</b>
                                        </small>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>
            
            <Card className={"mb-3"}>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div><b>Users</b></div>
                            {!loading && (
                                <small className="text-muted">Total: {inactiveUsers.length} users</small>
                            )}
                        </div>
                        {isExpanded && (
                            <div>
                                {statusFilter === 'ACTIVE' && (
                                    <>
                                        <Button 
                                            variant={fastMode ? "warning" : "outline-warning"}
                                            size="sm"
                                            className="mr-2"
                                            onClick={() => setFastMode(!fastMode)}
                                        >
                                            {fastMode ? "FM: ON" : "FM: OFF"}
                                        </Button>
                                        <Button
                                            variant={showBadges ? "primary" : "outline-primary"}
                                            size="sm"
                                            className="mr-2"
                                            onClick={() => setShowBadges(!showBadges)}
                                        >
                                            Badges
                                        </Button>
                                        <span className="mr-2">|</span>
                                    </>
                                )}
                                <Button 
                                    variant={statusFilter === null ? "primary" : "outline-primary"}
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => handleFilterChange(null)}
                                >
                                    All
                                </Button>
                                <Button 
                                    variant={statusFilter === 'ACTIVE' ? "primary" : "outline-primary"}
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => handleFilterChange('ACTIVE')}
                                >
                                    Active
                                </Button>
                            </div>
                        )}
                    </div>
                </Card.Header>
                <Card.Body style={{ padding: 0 }}>
                    {!isExpanded ? (
                        <div className="text-center p-3">
                            <Button variant="primary" onClick={handleLoad}>
                                Load
                            </Button>
                        </div>
                    ) : loading ? (
                        <div className="text-center p-3">Loading...</div>
                    ) : inactiveUsers.length === 0 ? (
                        <div className="text-center p-3">No users</div>
                    ) : (
                        <ListGroup variant="flush">
                            {inactiveUsers.map((user, index) => {
                                const lastSeenAny = user.lastSeenAt as any;
                                const lastSeen = lastSeenAny?.toDate ? lastSeenAny.toDate() : user.lastSeenAt;
                                const state = buttonStates[user.id] || {};
                                const isUnknownStatus = !user.status || (user.status !== 'ACTIVE' && user.status !== 'INACTIVE');
                                const showButtons = statusFilter !== 'NOT_SEEN' || user.status === 'ACTIVE';
                                const hasAdminBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.ADMIN && badge.status === BadgeStatus.ACTIVE);
                                const hasChampionBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.CHAMPION && badge.status === BadgeStatus.ACTIVE);
                                const hasSupporterBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.SUPPORTER && badge.status === BadgeStatus.ACTIVE);
                                const hasPassportBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.PASSPORT && badge.status === BadgeStatus.ACTIVE);
                                const showAdminBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.ADMIN);
                                const showChampionBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.CHAMPION);
                                const showSupporterBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.SUPPORTER);
                                const showPassportBadge = (user.badges || []).some(badge => badge.badgeType === BadgeType.PASSPORT);
                                const showAddAdminBadge = user.admin && !hasAdminBadge;
                                const showAddChampionBadge = user.champion && !hasChampionBadge;
                                const showAddSupporterBadge = user.supporter && !hasSupporterBadge;
                                const showAddPassportBadge = user.passport && !hasPassportBadge;
                                
                                // Determine if deactivate button should be disabled
                                let deactivateDisabled = state.deactivating || state.deactivated;
                                // Disable if user has any check-ins
                                deactivateDisabled = deactivateDisabled || (user.sessionCount || 0) > 0;
                                // Disable if user has more than 25 visits
                                deactivateDisabled = deactivateDisabled || (user.visitCount || 0) > 25;
                                if (statusFilter === 'NOT_SEEN') {
                                    // On NOT_SEEN tab: also disable if 6+ visits
                                    deactivateDisabled = deactivateDisabled || (user.visitCount || 0) >= 6;
                                } else if (statusFilter === 'ACTIVE') {
                                    // On ACTIVE tab: already checked sessionCount above
                                    deactivateDisabled = deactivateDisabled;
                                } else {
                                    // On All or other tabs: also disable if any visits
                                    deactivateDisabled = deactivateDisabled || (user.visitCount || 0) > 0;
                                }
                                
                                return (
                                    <ListGroup.Item key={user.id}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <Link to={`/profile/${user.id}/visits`} className="text-decoration-none">
                                                    <b>{user.name}{showAdminBadge ? " - Admin" : ""}{showChampionBadge ? " - Champion" : ""}{showSupporterBadge ? " - Supporter" : ""}{showPassportBadge ? " - Passport" : ""}</b>
                                                    {user.email && <span className="text-muted ml-2">({user.email})</span>}
                                                </Link>
                                                <div>
                                                    <small className="text-muted">
                                                        <FontAwesomeIcon icon={faCheckCircle} title="Visits" /> <b>{user.visitCount || 0}</b> | <FontAwesomeIcon icon={faLocationDot} title="Check-ins" /> <b>{Math.floor(user.sessionCount || 0)}</b> | Status: <b>{user.status || 'UNKNOWN'}</b> | Last Seen: <b>{lastSeen ? formatDate(lastSeen) : 'never'}</b>
                                                    </small>
                                                </div>
                                            </div>
                                            {showButtons && (
                                                <div className="d-flex align-items-center">
                                                    {showBadges && (
                                                        <>
                                                            <Button
                                                                variant={state.adminBadgeAdded ? "secondary" : "info"}
                                                                size="sm"
                                                                className="mr-2"
                                                                onClick={() => handleAddAdminBadge(user)}
                                                                disabled={showAdminBadge || state.addingAdminBadge || state.adminBadgeAdded}
                                                            >
                                                                <FontAwesomeIcon icon={faScrewdriverWrench} />
                                                            </Button>
                                                            <Button
                                                                variant={state.championBadgeAdded ? "secondary" : "warning"}
                                                                size="sm"
                                                                className="mr-2"
                                                                onClick={() => handleAddChampionBadge(user)}
                                                                disabled={showChampionBadge || state.addingChampionBadge || state.championBadgeAdded}
                                                            >
                                                                <FontAwesomeIcon icon={faTrophy} />
                                                            </Button>
                                                            <Button
                                                                variant={state.supporterBadgeAdded ? "secondary" : "success"}
                                                                size="sm"
                                                                className="mr-2"
                                                                onClick={() => handleAddSupporterBadge(user)}
                                                                disabled={showSupporterBadge || state.addingSupporterBadge || state.supporterBadgeAdded}
                                                            >
                                                                <FontAwesomeIcon icon={faBeerMugEmpty} />
                                                            </Button>
                                                            <Button
                                                                variant={state.passportBadgeAdded ? "secondary" : "primary"}
                                                                size="sm"
                                                                className="mr-2"
                                                                onClick={() => handleAddPassportBadge(user)}
                                                                disabled={showPassportBadge || state.addingPassportBadge || state.passportBadgeAdded}
                                                            >
                                                                <FontAwesomeIcon icon={faPassport} />
                                                            </Button>
                                                        </>
                                                    )}
                                                    {user.status !== 'ACTIVE' && statusFilter !== 'ACTIVE' && (
                                                        <Button 
                                                            variant={state.activated ? "secondary" : "success"}
                                                            size="sm"
                                                            className="mr-2"
                                                            onClick={() => handleActivate(user.id, user.name, index)}
                                                            disabled={state.activating || state.activated}
                                                        >
                                                            {state.activating ? "Activating..." : state.activated ? "Activated" : "Activate"}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="mr-2"
                                                        onClick={() => handleSetPassword(user)}
                                                    >
                                                        <FontAwesomeIcon icon={faKey} />
                                                    </Button>
                                                    {user.status !== 'INACTIVE' && (
                                                        <Button 
                                                            variant={state.deactivated ? "secondary" : "danger"}
                                                            size="sm"
                                                            onClick={() => handleDeactivate(user.id, user.name, index)}
                                                            disabled={deactivateDisabled}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>
        </>
    );
}

export default SuperAdminUsers;
